import { trackInternalEvent } from '@rio/tracking'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useState } from 'react'
import { CoverLetter, Resume } from 'packages/types'
import { useI18n } from 'builder/hooks/useI18n'
import DocumentActionMenu from 'builder/components/DocumentActionMenu'
import CoRegPopup, { CoRegPopupPositions } from 'builder/components/PreviewPanel/CoRegPopup'
import { ActionsButtonBadge } from 'builder/components/PreviewPanel/components/DownloadMenuLayout/styles'
import Icon from 'builder/components/Icon'
import { useConfig } from 'builder/hooks/useConfig'
import {
  DownloadDocumentHandler,
  selectors as resumeEditorSelectors,
} from 'builder/modules/resumeEditor'
import { selectors as renderingSelectors } from 'builder/modules/rendering'

import {
  actions as coRegActions,
  selectors as coRegSelectors,
} from 'builder/modules/coRegistration'
import { isResumeDocument } from 'builder/utils/getDocumentType'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import {
  DownloadButtonVersionOne,
  DownloadButtonVersionTwo,
  useDownloadButtonABTest,
} from 'builder/components/PreviewPanel/components/DownloadButtonExperiment'

import { StartTailoringButton } from 'builder/components/DocumentEditor/TailoringFromPublicJobBoard'
import {
  ToolbarRight as Container,
  Updating,
  UpdatingIcon,
  UpdatingText,
  Actions,
  ActionsButton,
  DownloadButton,
} from '../../styles'

interface ToolbarRightProps {
  currentDocument: Resume | CoverLetter
  handleDownloadDocument: DownloadDocumentHandler
  isSyncing: boolean
}

const ToolbarRight = (props: ToolbarRightProps) => {
  const { currentDocument, handleDownloadDocument, isSyncing } = props
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const config = useConfig()
  const [isActionMenuOpen, setIsActionMenuOpen] = useState<boolean>(false)
  const type = currentDocument.type
  const isResume = isResumeDocument(currentDocument)
  const isCoRegPopupOpen = useTypedSelector(coRegSelectors.isPopupOpen)
  const isReadyToCoReg = useTypedSelector(coRegSelectors.isResumeReady)
  const showOneDownloadButton = !!(config?.features.downloadButtonLayout === 'one_button')
  const showUpdatedMenuLayout = isResume && showOneDownloadButton
  const isDownloading = useSelector(renderingSelectors.isDocumentDownloading)
  const isPreviewGenerating = useSelector(renderingSelectors.isPreviewGenerating)
  const isUpdating = isSyncing || isPreviewGenerating
  const { isDownloadButtonABTestActive, downloadButtonABTestVersion } = useDownloadButtonABTest()

  const toggleActionMenu = useCallback(() => {
    if (!isActionMenuOpen) {
      trackInternalEvent('open_document_action_menu', {
        source: `full_screen_${type}_preview`,
      })
    }
    setIsActionMenuOpen(!isActionMenuOpen)
  }, [isActionMenuOpen, type])

  const handleShareClick = useCallback(() => {
    trackInternalEvent('click_share_button', { source: `full_screen_${type}_preview` })
  }, [type])

  const onDownloadButtonClick = () => {
    showUpdatedMenuLayout ? toggleActionMenu() : handleDownloadDocument({})
  }

  const autoTailoringFromPublicJobBoard = useTypedSelector(
    resumeEditorSelectors.autoTailoringFromPublicJobBoard,
  )

  return (
    <Container>
      <Updating isVisible={isUpdating}>
        <UpdatingIcon />
        <UpdatingText>{i18n.t('builder.resume_editor.updating')}</UpdatingText>
      </Updating>

      {autoTailoringFromPublicJobBoard.enabled ? (
        <StartTailoringButton />
      ) : isDownloadButtonABTestActive && isResume ? (
        downloadButtonABTestVersion === 'v1' ? (
          <DownloadButtonVersionOne
            documentId={currentDocument.id}
            onDownload={handleDownloadDocument}
            documentType={type}
            onShare={handleShareClick}
            template={currentDocument.template}
            isPreviewLayout
          />
        ) : (
          <DownloadButtonVersionTwo
            documentId={currentDocument.id}
            onDownload={handleDownloadDocument}
            documentType={type}
            onShare={handleShareClick}
            template={currentDocument.template}
            isPreviewLayout
          />
        )
      ) : (
        <>
          <DownloadButton onClick={onDownloadButtonClick} isDisabled={isDownloading}>
            {i18n.t(
              `builder.resume_editor.${
                showUpdatedMenuLayout ? 'download_document_menu.button_text' : 'download'
              }`,
            )}
          </DownloadButton>
          <Actions>
            {!showUpdatedMenuLayout && (
              <ActionsButton onClick={toggleActionMenu} isDisabled={isDownloading}>
                <Icon.More />
              </ActionsButton>
            )}

            <ActionsButtonBadge
              isVisible={isReadyToCoReg && !isActionMenuOpen && !isCoRegPopupOpen}
            />

            {isActionMenuOpen && (
              <DocumentActionMenu
                position="bottom"
                documentType={type}
                documentId={currentDocument.id}
                onClose={toggleActionMenu}
                onDownload={handleDownloadDocument}
                onShare={handleShareClick}
              />
            )}

            {isCoRegPopupOpen && (
              <CoRegPopup
                position={CoRegPopupPositions.bottom}
                onClick={() =>
                  dispatch(coRegActions.open({ resumeId: currentDocument.id, source: 'pop_up' }))
                }
                onClose={() => dispatch(coRegActions.togglePopup(false))}
              />
            )}
          </Actions>
        </>
      )}
    </Container>
  )
}

export default ToolbarRight
